import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import GetAPI from "../../APICalls/GetQuery";

const PAGE_SIZE = 9;
const ROTATE_INTERVAL = 5000;

export default function TVPresentation() {
  const { clientID } = useParams();
  console.log("clientID ", clientID);
  const [clientDetails, setClientDetails] = useState(null);
  const [orders, setOrders] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [clock, setClock] = useState("");

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const rotateRef = useRef(null);

  let environment = process.env.REACT_APP_NODE_ENV;
  let GetClientDetailsAPI, QSROrdersAPI;
  if (environment?.toUpperCase() === "PRODUCTION") {
    QSROrdersAPI = process.env.REACT_APP_PROD_QSR_ORDERS_API;
    GetClientDetailsAPI = process.env.REACT_APP_PROD_QSR_KITCHEN_API;
  } else {
    QSROrdersAPI = process.env.REACT_APP_DEV_QSR_ORDERS_API;
    GetClientDetailsAPI = process.env.REACT_APP_DEV_QSR_KITCHEN_API;
  }

  useEffect(() => {
    getClientDetails();
    const t = setInterval(() => {
      const d = new Date();
      setClock(
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const connectWebSocket = (clientID) => {
    if (!clientID || wsRef.current) return;
    const ws = new WebSocket(
      `${process.env.REACT_APP_QSR_WS_URL}?clientID=${clientID}`
    );

    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (!message?.order) return;

        setOrders((prev) => {
          const idx = prev.findIndex(
            (o) => o.orderID === message.order.orderID
          );
          if (idx !== -1) {
            const copy = [...prev];
            copy[idx] = message.order;
            return copy;
          }
          return [message.order, ...prev];
        });
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      reconnectRef.current = setTimeout(
        () => connectWebSocket(clientID),
        3000
      );
    };

    ws.onerror = () => ws.close();
  };

  const getClientDetails = async () => {
    if (!clientID) return;
    try {
      const res = await GetAPI(GetClientDetailsAPI, { clientID });
      if (res?.data?.statusCode === 200) {
        setClientDetails(res.data.body || []);
      }
    } catch {
      setClientDetails(null);
    }
  };

  const getKitchenOrders = async () => {
    if (!clientID) return;
    try {
      const res = await GetAPI(QSROrdersAPI, { clientID });
      if (res?.data?.statusCode === 200) {
        setOrders(res.data.body || []);
      } else {
        setOrders([]);
      }
    } catch {
      setOrders([]);
    }
  };

  useEffect(() => {
    if (!clientID) return;

    getKitchenOrders();
    connectWebSocket(clientID);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (rotateRef.current) clearInterval(rotateRef.current);
    };
  }, [clientID]);

  const processedOrders = useMemo(() => {
    return orders
      .filter((o) => {
        const status = o.status?.toUpperCase();
        return status === "READY" || status === "NEW";
      })
      .map((o) => {
        const status = o.status?.toUpperCase();

        let priority = 1;
        if (status === "READY") priority = 0;

        let eta = "-";

        return {
          token: `#${o.orderNumber}`,
          name: o.craverName,
          status,
          priority,
          eta,
          items: o.orderedItems || [],
        };
      })
      .sort((a, b) => a.priority - b.priority);
  }, [orders, now]);

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < processedOrders.length; i += PAGE_SIZE) {
      chunks.push(processedOrders.slice(i, i + PAGE_SIZE));
    }
    return chunks.length ? chunks : [[]];
  }, [processedOrders]);

  useEffect(() => {
    if (pages.length <= 1) return;

    rotateRef.current = setInterval(() => {
      setPageIndex((p) => (p + 1) % pages.length);
    }, ROTATE_INTERVAL);

    return () => {
      if (rotateRef.current) clearInterval(rotateRef.current);
    };
  }, [pages.length]);

  const visibleOrders = pages[pageIndex] || [];

  const readyCount = processedOrders.filter((o) => o.status === "READY").length;

  const cookingCount = processedOrders.filter((o) => o.status === "NEW").length;

  const rowsToRender = useMemo(() => {
    const filled = [...visibleOrders];

    while (filled.length < 9) {
      filled.push(null);
    }

    return filled.slice(0, 9);
  }, [visibleOrders]);

  return (
    <div className="w-screen h-screen bg-[#F8FAFC] flex flex-col overflow-hidden">
      <div className="flex-1">
        <div className="w-full h-full rounded-2xl border border-gray-200 bg-white flex flex-col">
          <div className="py-3 px-6 flex items-center justify-between border-b border-gray-200">
            <div>
              <h2 className="text-[40px] font-bold text-gray-900 flex items-center gap-2">
                {clientDetails?.clientName}
                <img
                  src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/cook.png"
                  className="w-12 h-12"
                  alt=""
                />
              </h2>
            </div>

            <div className="flex items-center gap-3 -mt-1">
              <span className="px-4 py-1.5 rounded-xl bg-green-100 text-green-700 font-semibold text-lg">
                Ready: {readyCount}
              </span>
              <span className="px-4 py-1.5 rounded-xl bg-orange-100 text-orange-700 font-semibold text-lg">
                Preparing: {cookingCount}
              </span>
              <span className="px-4 py-1.5 rounded-full bg-gray-100 text-gray-800 font-semibold text-lg">
                {clock}
              </span>
            </div>
          </div>
          <div
          key={pageIndex}
          className="flex-1 grid grid-cols-3 gap-4 px-6 py-1
                    auto-rows-max
                    transition-all duration-700 ease-in-out"
        >

           {rowsToRender.map((row, i) => (
            <div
              key={i}
              className={`rounded-xl border-solid border-2 p-4 flex flex-col justify-between bg-white
                          h-[120px]
                ${
                  row?.status?.toUpperCase() === "READY"
                    ? "border-green-600"
                    : row?.status?.toUpperCase() === "NEW"
                    ? "border-orange-500"
                    : "border-gray-300"
                }
              `}
            >
              {row ? (
                <>
                  <div className="text-4xl font-extrabold text-[#3B5BDB]">
                    {row.token}
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    {row.status?.toUpperCase() === "READY" ? (
                      <div className="flex items-center gap-2 text-green-600 font-semibold text-[20px]">
                        <img
                          src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/ready.png"
                          className="w-6 h-6"
                          alt=""
                        />
                        Ready to collect
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600 font-semibold text-[20px]">
                        <img
                          src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/preparing.png"
                          className="w-6 h-6"
                          alt=""
                        />
                        Preparing
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="h-full w-full opacity-0">—</div>
              )}
            </div>
          ))}

          </div>
        </div>
      </div>
    </div>
  );
}
